import { AnimalType } from './animal';

/**
 * The medication catalogue, sourced from the Dutch pig register.
 *
 * A register record is a pack, not a medicine: one medication covers several packs of different
 * sizes. Withdrawal periods belong to the medication rather than the pack, because two packs that
 * disagree on one are different medications.
 */
export interface MedicationPack {
  id: string;
  internalCode: number;
  eanCode?: string | null;
  productName: string;
  registrationNumber: string;
  packaging?: string | null;
  packagingCount?: number | null;
  producer?: string | null;
}

export interface MedicationWithdrawalPeriod {
  id: string;
  animalType: AnimalType;
  /**
   * Null means the register stated no usable number. It is not zero - a zero clears an animal for
   * slaughter, and the two have to read differently on screen.
   */
  days?: number | null;
  /** The register's own value, kept verbatim, including the ones that are not numbers. */
  daysRaw: string;
  /** The register flagged the period as not expressible as plain days. Does not void `days`. */
  isComplex: boolean;
  complexText?: string | null;
  sourceGroupId: number;
  sourceGroupLabel: string;
}

export interface MedicationSubstance {
  id: string;
  /** The substance's own name, e.g. `ampicilline`. */
  name: string;
  /** Null where the register stated no strength. */
  amount?: number | null;
  unit?: string | null;
  /** What the strength is per — `cc/ml`, `gram`, `Pipet`. */
  dosageForm?: string | null;
}

export interface Medication {
  id: string;
  /** What the NFC sticker carries: ASCII, at most 19 characters. */
  shortName: string;
  officialName: string;
  atcCodes?: string | null;
  channelling?: string | null;
  route?: string | null;
  dosageForm?: string | null;
  source: string;
  sourceVersion?: number | null;
  verifiedAt?: string | null;
  /** No usable period for any animal type. Distinct from a period of zero days. */
  hasUnestablishedWithdrawalPeriod: boolean;
  packs: MedicationPack[];
  withdrawalPeriods: MedicationWithdrawalPeriod[];
  substances: MedicationSubstance[];
}

export interface MedicationsResult {
  items: Medication[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
}

/**
 * The five pig life stages a withdrawal period can be stated for, in barn order.
 *
 * Label them with `getAnimalTypeName` from `utils/animalutils` rather than a key of their own.
 */
export const MEDICATION_ANIMAL_TYPES = [
  AnimalType.Piglet,
  AnimalType.Fattening,
  AnimalType.Gilt,
  AnimalType.Sow,
  AnimalType.Boar
];
